<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t('reports.farmProductivity.title') }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t('reports.farmProductivity.aiReportGuide') }}
        </v-btn>
      </div>
      <div>
        <v-btn outlined color="primary" class="mr-2" @click="cancel">
          {{ $t('reports.farmProductivity.cancel') }}
        </v-btn>
        <v-btn color="primary" depressed :disabled="!canGenerate" :loading="generating" @click="generate">
          {{ $t('reports.farmProductivity.generateReport') }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined>
      <v-row>
        <!-- Map -->
        <v-col cols="12" md="8">
          <h3 class="mb-1 black--text">{{ $t('reports.farmProductivity.selectFarmGeofence') }}</h3>
          <p class="mb-3 grey--text">{{ $t('reports.farmProductivity.selectFarmGeofenceHelp') }}</p>
          <geofence-map
            :source="source"
            :zone="selectedZone"
            :geojson-coordinates="geojsonCoords"
            :radius-meters="circleRadius"
            @update:geometry="onDrawGeometry"
          />
        </v-col>

        <!-- Right panel -->
        <v-col cols="12" md="4">
          <v-btn-toggle v-model="source" mandatory dense class="flex-wrap mb-4 d-flex">
            <v-btn value="farm_zone">{{ $t('reports.farmProductivity.sourceFarmZone') }}</v-btn>
            <v-btn value="polygon">{{ $t('reports.farmProductivity.sourcePolygon') }}</v-btn>
            <v-btn value="circle">{{ $t('reports.farmProductivity.sourceCircle') }}</v-btn>
            <v-btn value="geojson">{{ $t('reports.farmProductivity.sourceGeojson') }}</v-btn>
          </v-btn-toggle>

          <!-- Farm/Zone -->
          <div v-if="source === 'farm_zone'">
            <label class="mb-1 d-block black--text">{{ $t('reports.farmProductivity.selectFarmZone') }}</label>
            <farm-zone-select v-model="selectedZone" />
          </div>

          <!-- Draw polygon / circle: instructions -->
          <div v-else-if="source === 'polygon'">
            <v-alert dense text type="info" class="mb-0">
              {{ $t('reports.farmProductivity.drawPolygon') }} — {{ $t('reports.farmProductivity.drawPolygonHelp') }}
            </v-alert>
          </div>
          <div v-else-if="source === 'circle'">
            <v-alert dense text type="info" class="mb-3">
              {{ $t('reports.farmProductivity.drawCircle') }} — {{ $t('reports.farmProductivity.drawCircleHelp') }}
            </v-alert>
            <v-text-field
              v-model.number="circleRadius"
              :label="$t('reports.farmProductivity.radiusM')"
              type="number"
              min="1"
              suffix="m"
              outlined
              dense
              hide-details
            />
          </div>

          <!-- Upload GeoJSON -->
          <div v-else-if="source === 'geojson'">
            <h4 class="mb-1 primary--text">{{ $t('reports.farmProductivity.uploadGeojsonFile') }}</h4>
            <p class="mb-2 grey--text text-caption">{{ $t('reports.farmProductivity.uploadGeojsonHelp') }}</p>
            <v-file-input
              accept=".geojson,.json"
              :label="$t('reports.farmProductivity.selectAFile')"
              prepend-icon="mdi-upload"
              outlined
              dense
              hide-details
              :loading="uploadingGeojson"
              @change="onGeojsonUpload"
            />
          </div>
        </v-col>
      </v-row>
      <!-- Estimated compute cost -->
      <estimated-compute-cost report-type="farm_productivity" :geofence="geofencePayload" />
    </v-card>

    <guide-dialog :value="showGuide" @input="showGuide = $event" />
  </div>
</template>

<script>
import FarmProductivityService from '@/_services/FarmProductivityService';
import S3UploadService from '@/_services/S3UploadService';
// Reuse the geofence map + farm/zone selector from Crop Progression — they are
// feature-agnostic geofence pickers shared across the AI report modules.
import GeofenceMap from '../crop-progression/components/GeofenceMap.vue';
import FarmZoneSelect from '../crop-progression/components/FarmZoneSelect.vue';
import GuideDialog from './components/GuideDialog.vue';
import EstimatedComputeCost from '../shared/EstimatedComputeCost.vue';
import reportError from '@/mixins/reportError';

const DEFAULT_CIRCLE_RADIUS_M = 20; // meters

export default {
  name: 'FarmProductivityCreate',
  components: { GeofenceMap, FarmZoneSelect, GuideDialog, EstimatedComputeCost },
  mixins: [reportError],
  data() {
    return {
      source: 'farm_zone',
      selectedZone: null,
      circleRadius: DEFAULT_CIRCLE_RADIUS_M, // meters; two-way synced with the map circle
      drawnGeometry: null,
      geojsonCoords: [],
      geojsonS3Path: null,
      uploadingGeojson: false,
      generating: false,
      showGuide: false,
    };
  },
  computed: {
    geofencePayload() {
      switch (this.source) {
        case 'farm_zone':
          return this.selectedZone
            ? {
                geofenceType: 'farm_zone',
                geofenceId: this.selectedZone.geofenceId,
                farmId: this.selectedZone.farmId,
              }
            : null;
        case 'polygon':
          return this.drawnGeometry && this.drawnGeometry.shape === 'polygon'
            ? { geofenceType: 'polygon', coordinates: this.drawnGeometry.coordinates }
            : null;
        case 'circle':
          return this.drawnGeometry && this.drawnGeometry.shape === 'circle'
            ? {
                geofenceType: 'circle',
                latitude: this.drawnGeometry.latitude,
                longitude: this.drawnGeometry.longitude,
                radius: this.drawnGeometry.radius,
              }
            : null;
        case 'geojson':
          return this.geojsonCoords.length >= 3
            ? {
                geofenceType: 'geojson',
                coordinates: this.geojsonCoords,
                geojsonS3Path: this.geojsonS3Path,
              }
            : null;
        default:
          return null;
      }
    },
    hasGeofence() {
      return !!this.geofencePayload;
    },
    canGenerate() {
      return this.hasGeofence && !this.generating;
    },
  },
  watch: {
    source() {
      // Reset per-source state when switching source.
      this.drawnGeometry = null;
      if (this.source !== 'geojson') {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
      }
      if (this.source !== 'farm_zone') this.selectedZone = null;
      if (this.source === 'circle') this.circleRadius = DEFAULT_CIRCLE_RADIUS_M; // default radius
    },
  },
  methods: {
    onDrawGeometry(geom) {
      this.drawnGeometry = geom;
      // Map circle -> radius field (guard against feedback loop).
      if (geom && geom.shape === 'circle' && geom.radius != null) {
        const meters = Math.round(geom.radius * 1000);
        if (Math.abs(meters - Number(this.circleRadius)) >= 1) {
          this.circleRadius = meters;
        }
      }
    },
    async onGeojsonUpload(file) {
      if (!file) {
        this.geojsonCoords = [];
        this.geojsonS3Path = null;
        return;
      }
      const text = await file.text().catch(() => null);
      if (!text) return;
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        this.$notify({
          title: this.$t('reports.farmProductivity.invalidGeojsonFile'),
          text: this.$t('reports.farmProductivity.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      const coords = this.extractPolygonFromGeojson(parsed);
      if (coords.length < 3) {
        this.$notify({
          title: this.$t('reports.farmProductivity.invalidGeojsonFile'),
          text: this.$t('reports.farmProductivity.invalidGeojsonFileDescription'),
          type: 'error',
        });
        return;
      }
      this.geojsonCoords = coords;
      // Upload the raw file to S3 via pre-signed URL and store the object key
      this.uploadingGeojson = true;
      try {
        const { key } = await S3UploadService.singleUpload(file, 'farm-productivity/geojson');
        this.geojsonS3Path = key;
      } catch (e) {
        this.$notify({
          text: this.$t('reports.farmProductivity.uploadFailed'),
          type: 'error',
        });
      } finally {
        this.uploadingGeojson = false;
      }
    },
    extractPolygonFromGeojson(geojson) {
      const ringToLatLng = (ring) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
      try {
        if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length) {
          return this.extractPolygonFromGeojson(geojson.features[0]);
        }
        const geom = geojson.type === 'Feature' ? geojson.geometry : geojson;
        if (!geom) return [];
        if (geom.type === 'Polygon') return ringToLatLng(geom.coordinates[0]);
        if (geom.type === 'MultiPolygon') return ringToLatLng(geom.coordinates[0][0]);
      } catch (e) {
        return [];
      }
      return [];
    },
    async generate() {
      if (!this.canGenerate) return;
      this.generating = true;
      try {
        const env = await FarmProductivityService.generateReport(this.geofencePayload);
        const report = env && env.data && env.data.report;
        if (report && report.reportUid) {
          this.$router.push({
            name: 'farmProductivityView',
            params: { reportUid: report.reportUid },
          });
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, 'reports.farmProductivity.generateFailed'),
            type: 'error',
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, 'reports.farmProductivity.generateFailed'),
          type: 'error',
        });
      } finally {
        this.generating = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'farmProductivityList' });
    },
  },
};
</script>

<style scoped>
.compute-card {
  background: #f4fbf7;
}
.how-to {
  border: 1px solid #ffe1a6;
}
.how-to a {
  cursor: pointer;
}
</style>
